import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { getAuthConfig } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Check, CreditCard, Lock, ArrowLeft, Truck, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SERVICE_FEE_PERCENTAGE = 0.03; // 3%

// US State options for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' }
];

function ShipNow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [shippingCalculation, setShippingCalculation] = useState(location.state?.shippingCalculation);
  const [calculatingShipping, setCalculatingShipping] = useState(false);

  const pledgeData = location.state?.pledgeData;

  // Address form state - pre-populate from checkout if available
  const [address, setAddress] = useState(location.state?.address || {
    street: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [saveAddress, setSaveAddress] = useState(true);
  
  // Guest checkout fields - pre-populate from checkout if available
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  // Insurance option
  const [hasInsurance, setHasInsurance] = useState(false);
  
  // Tax state
  const [taxData, setTaxData] = useState(null);
  const [calculatingTax, setCalculatingTax] = useState(false);

  useEffect(() => {
    // Fetch user data (try localStorage token first, then cookie)
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/me`, getAuthConfig());
        setUser(response.data);
        if (!email) setEmail(response.data.email || '');
        
        // If user has saved address and no address from checkout, populate form
        if (response.data.saved_address && !location.state?.address) {
          setAddress(response.data.saved_address);
        }
      } catch (error) {
        console.error('Not logged in - guest checkout mode');
        setUser(null);
      }
    };

    fetchUser();

    // Redirect if no pledge data
    if (!pledgeData) {
      navigate('/products');
    }
  }, [pledgeData, navigate, email, location.state?.address]);

  // Check if email already has an account
  useEffect(() => {
    if (user || !email || !email.includes('@')) {
      setEmailStatus(null);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/check-email`, { email });
        setEmailStatus(res.data.exists ? 'exists' : 'available');
      } catch {
        setEmailStatus(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [email, user]);

  // Calculate shipping when ZIP code is entered (or on load if flag is set)
  useEffect(() => {
    const calculateShipping = async () => {
      if (address.zip_code && address.zip_code.length === 5 && pledgeData && !shippingCalculation) {
        setCalculatingShipping(true);
        try {
          const response = await axios.post(`${BACKEND_URL}/api/shipping/calculate`, {
            quantity_sqft: pledgeData.sqft,
            zip_code: address.zip_code,
            material_type: pledgeData.product_name.toLowerCase().includes('hardwood') ? 'hardwood' : 'lvp'
          });
          setShippingCalculation(response.data);
        } catch (error) {
          console.error('Error calculating shipping:', error);
          toast.error('Could not calculate shipping costs. Please check your ZIP code.');
        } finally {
          setCalculatingShipping(false);
        }
      }
    };

    const debounceTimer = setTimeout(calculateShipping, 800);
    return () => clearTimeout(debounceTimer);
  }, [address.zip_code, pledgeData, shippingCalculation]);

  // Calculate tax when state changes
  useEffect(() => {
    const calculateTax = async () => {
      if (address.state && address.state.length === 2 && pledgeData) {
        setCalculatingTax(true);
        try {
          // For LCL, tax is on full amount (product + shipping + insurance)
          const productCost = parseFloat(pledgeData.total_price);
          const shippingCost = shippingCalculation ? parseFloat(shippingCalculation.lcl.total) : 0;
          const insuranceCost = hasInsurance ? ((productCost + shippingCost) * 0.02) : 0;
          const subtotal = productCost + shippingCost + insuranceCost;
          
          const response = await axios.get(`${BACKEND_URL}/api/tax/calculate`, {
            params: {
              state: address.state.toUpperCase(),
              subtotal: subtotal
            }
          });
          setTaxData(response.data);
        } catch (error) {
          console.error('Error calculating tax:', error);
          setTaxData(null);
        } finally {
          setCalculatingTax(false);
        }
      } else {
        setTaxData(null);
      }
    };

    const debounceTimer = setTimeout(calculateTax, 500);
    return () => clearTimeout(debounceTimer);
  }, [address.state, pledgeData, shippingCalculation, hasInsurance]);

  if (!pledgeData) {
    return null;
  }

  // Calculate costs
  const productCost = parseFloat(pledgeData.total_price);
  const shippingCost = shippingCalculation ? parseFloat(shippingCalculation.lcl.total) : 0;
  const insuranceCost = hasInsurance ? ((productCost + shippingCost) * 0.02) : 0;
  const subtotal = productCost + shippingCost + insuranceCost;
  const taxAmount = taxData?.tax_amount || 0;
  const subtotalWithTax = subtotal + taxAmount;
  const serviceFee = subtotalWithTax * SERVICE_FEE_PERCENTAGE;
  const totalAmount = subtotalWithTax + serviceFee;

  const handleFullPayment = async () => {
    // Validate email
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate account creation (if not logged in)
    if (!user && emailStatus !== 'exists') {
      if (!password || password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    if (emailStatus === 'exists' && !user) {
      toast.error('This email already has an account. Please log in first.');
      return;
    }

    // Validate address
    if (!address.street || !address.city || !address.state || !address.zip_code) {
      toast.error('Please complete all address fields');
      return;
    }

    if (address.zip_code.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (!shippingCalculation) {
      toast.error('Please wait for shipping calculation to complete');
      return;
    }

    setProcessing(true);

    try {
      // Create Stripe checkout session
      const response = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, {
        order_type: 'lcl_full',
        product_cost: productCost,
        shipping_cost: shippingCost,
        insurance_cost: insuranceCost,
        tax_amount: taxAmount,
        state: address.state.toUpperCase(),
        origin_url: window.location.origin,
        email: email,
        create_account_password: !user && emailStatus !== 'exists' ? password : undefined,
        pledge_data: {
          ...pledgeData,
          shipping_address: address,
          has_insurance: hasInsurance,
          insurance_cost: insuranceCost
        }
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setProcessing(false);
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Could not initiate payment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-36 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/checkout', { state: { pledgeData } })}
              className="flex items-center text-gray-600 hover:text-primary mb-4"
              data-testid="back-to-checkout"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Checkout
            </button>
            <div className="flex items-center mb-4">
              <Truck className="w-10 h-10 text-primary mr-4" />
              <div>
                <h1 className="font-bold text-4xl mb-1">Ship Immediately</h1>
                <p className="text-gray-600">Pay in full and skip the container wait</p>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mb-8 p-6 bg-orange-50 border-2 border-orange-200 rounded-3xl">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg text-orange-900 mb-2">Direct Shipment (LCL)</h3>
                <p className="text-sm text-orange-800 mb-2">
                  Your order will be shipped directly without waiting for a shared container to fill:
                </p>
                <ul className="text-sm text-orange-800 space-y-1 ml-4">
                  <li>✓ Ships within 3-5 business days</li>
                  <li>✓ Estimated delivery: ~{shippingCalculation ? shippingCalculation.lcl.delivery_days : '30'} days total</li>
                  <li>✓ No waiting for other orders</li>
                  <li>✓ Priority handling and tracking</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Summary */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <h2 className="font-bold text-2xl mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Product</span>
                    <span className="font-semibold">{pledgeData.product_name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Quantity</span>
                    <span className="font-semibold">{parseInt(pledgeData.sqft).toLocaleString()} sqft</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Product Cost</span>
                    <span className="font-semibold">${productCost.toFixed(2)}</span>
                  </div>
                  {calculatingShipping ? (
                    <div className="flex justify-between py-3 border-b bg-blue-50 -mx-4 px-4 rounded-lg">
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span className="text-sm text-gray-600">Calculating shipping...</span>
                      </div>
                    </div>
                  ) : shippingCalculation ? (
                    <div className="flex justify-between py-3 border-b bg-orange-50 -mx-4 px-4 rounded-lg">
                      <div>
                        <p className="font-semibold text-orange-900">Direct Shipping (LCL)</p>
                        <p className="text-xs text-orange-700">Priority delivery to {shippingCalculation.port.name}</p>
                      </div>
                      <span className="font-semibold text-orange-900">${shippingCost.toFixed(2)}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between py-3 border-b bg-yellow-50 -mx-4 px-4 rounded-lg">
                      <p className="text-sm text-yellow-800">Enter your ZIP code below to calculate shipping</p>
                    </div>
                  )}
                  {hasInsurance && (
                    <div className="flex justify-between py-3 border-b">
                      <span className="text-gray-600">Shipping Insurance (2%)</span>
                      <span className="font-semibold">${insuranceCost.toFixed(2)}</span>
                    </div>
                  )}
                  {taxAmount > 0 && (
                    <div className="flex justify-between py-3 border-b">
                      <div className="flex items-center">
                        <span className="text-gray-600">Sales Tax ({taxData?.tax_rate_display || '0%'})</span>
                        <div className="relative ml-2 group">
                          <Info className="w-4 h-4 text-gray-400 cursor-help" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {address.state} state sales tax
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold">${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {taxData?.tax_rate === 0 && address.state && (
                    <div className="flex justify-between py-3 border-b bg-green-50 -mx-4 px-4 rounded-lg">
                      <span className="text-green-700 font-semibold">No Sales Tax ({address.state})</span>
                      <span className="text-green-600 font-semibold">$0.00</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 border-b">
                    <div className="flex items-center">
                      <span className="text-gray-600">Service Fee (3%)</span>
                      <div className="relative ml-2 group">
                        <Info className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Covers credit card processing charges
                        </div>
                      </div>
                    </div>
                    <span className="font-semibold">${serviceFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-xl font-bold">Total Amount</span>
                    <span className="text-3xl font-bold text-primary">${totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Address Form */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <h2 className="font-bold text-2xl mb-6">Delivery Address</h2>

                <div className="space-y-4">
                  {/* Email field */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full p-3 border-2 rounded-xl focus:outline-none ${
                        emailStatus === 'exists' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 focus:border-primary'
                      }`}
                      placeholder="your.email@example.com"
                      required
                      disabled={!!user}
                      data-testid="email-input"
                    />
                    {!user && checkingEmail && <p className="text-xs text-gray-400 mt-1">Checking...</p>}
                    {!user && emailStatus === 'exists' && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm font-semibold text-amber-800">This email already has an account.</p>
                        <button
                          onClick={() => navigate('/my-orders', { state: { returnTo: '/ship-now' } })}
                          className="text-sm font-bold text-primary hover:underline mt-1"
                        >
                          Log in to continue checkout
                        </button>
                      </div>
                    )}
                    {user && <p className="text-xs text-green-600 mt-1 font-semibold">Logged in as {user.name || user.email}</p>}
                  </div>

                  {/* Password fields — only show for new users */}
                  {!user && emailStatus !== 'exists' && email.includes('@') && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                      <p className="text-sm font-semibold text-blue-800">Create your account to track orders & pay balances</p>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Password *</label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="Min 6 characters"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Confirm Password *</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`w-full p-3 border-2 rounded-xl focus:outline-none ${
                            confirmPassword && password !== confirmPassword ? 'border-red-400' : 'border-gray-200 focus:border-primary'
                          }`}
                          placeholder="Re-enter password"
                        />
                        {confirmPassword && password !== confirmPassword && (
                          <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold mb-2">Street Address *</label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="123 Main St"
                      required
                      data-testid="street-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">City *</label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="Des Moines"
                        required
                        data-testid="city-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">State *</label>
                      <select
                        value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none bg-white"
                        required
                        data-testid="state-input"
                      >
                        <option value="">Select</option>
                        {US_STATES.map((s) => (
                          <option key={s.code} value={s.code}>{s.code}</option>
                        ))}
                      </select>
                      {taxData && (
                        <p className="text-xs text-gray-500 mt-1">
                          Sales tax: {taxData.tax_rate_display}
                          {taxData.tax_rate === 0 && <span className="text-green-600 font-semibold"> (No sales tax!)</span>}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      value={address.zip_code}
                      onChange={(e) => {
                        const newZip = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setAddress({ ...address, zip_code: newZip });
                        // Clear shipping calculation if ZIP changes
                        if (newZip !== address.zip_code) {
                          setShippingCalculation(null);
                        }
                      }}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="50309"
                      maxLength="5"
                      required
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              </div>

              {/* Insurance Option */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <div className="flex items-center mb-4">
                  <Check className="w-6 h-6 text-primary mr-3" />
                  <h2 className="font-bold text-2xl">Shipping Insurance</h2>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl mb-4">
                  <p className="text-sm text-gray-700 mb-2">
                    Protect your investment with shipping insurance
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-4">
                    <li>• Covers loss or damage during transit</li>
                    <li>• Only 2% of total order value (product + shipping)</li>
                    <li>• Full replacement or refund if anything happens</li>
                  </ul>
                </div>

                <div className="flex items-start p-4 border-2 border-gray-200 rounded-xl hover:border-primary transition-colors">
                  <input
                    type="checkbox"
                    id="hasInsurance"
                    checked={hasInsurance}
                    onChange={(e) => setHasInsurance(e.target.checked)}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary mt-0.5"
                    data-testid="insurance-checkbox"
                  />
                  <label htmlFor="hasInsurance" className="ml-3 flex-1 cursor-pointer">
                    <p className="font-semibold text-gray-900">Yes, add shipping insurance</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {shippingCalculation ? (
                        <>
                          Cost: <span className="font-semibold text-primary">
                            ${((productCost + shippingCost) * 0.02).toFixed(2)}
                          </span> (2% of ${(productCost + shippingCost).toFixed(2)})
                        </>
                      ) : (
                        'Calculate shipping first to see insurance cost'
                      )}
                    </p>
                  </label>
                </div>
              </div>
            </div>

            {/* Payment Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100 sticky top-28">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                    <CreditCard className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Full Payment</h3>
                  <p className="text-sm text-gray-600">Powered by Stripe</p>
                </div>

                {/* Amount Breakdown */}
                <div className="mb-6 p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span>Product Total:</span>
                      <span className="font-semibold">${productCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Shipping (LCL):</span>
                      <span className="font-semibold">${shippingCost.toFixed(2)}</span>
                    </div>
                    {hasInsurance && (
                      <div className="flex justify-between text-sm">
                        <span>Insurance (2%):</span>
                        <span className="font-semibold">${insuranceCost.toFixed(2)}</span>
                      </div>
                    )}
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Sales Tax ({taxData?.tax_rate_display}):</span>
                        <span className="font-semibold">${taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Service Fee (3%):</span>
                      <span>${serviceFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-orange-200">
                      <span className="font-bold">Total Due Today:</span>
                      <span className="font-bold text-2xl text-orange-600">${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Pay Button */}
                <Button
                  onClick={handleFullPayment}
                  disabled={processing || !shippingCalculation || !email || !address.state}
                  className="w-full h-14 text-lg rounded-full bg-orange-600 hover:bg-orange-700"
                  data-testid="pay-full-button"
                >
                  {processing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Pay ${totalAmount.toFixed(2)} Now
                    </>
                  )}
                </Button>

                {!shippingCalculation && (
                  <p className="text-xs text-center text-orange-600 mt-3">
                    Enter ZIP code to calculate shipping
                  </p>
                )}

                {/* Benefits */}
                <div className="mt-6 space-y-2">
                  <div className="flex items-center text-xs text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                    Ships in 3-5 business days
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                    Priority handling & tracking
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                    No container wait time
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                    Powered by Stripe
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShipNow;
